import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Generator } from '../hooks/useGenerators';
import { getIndustry, getIndustryLabel } from '../lib/industries';
import { buildPublicGeneratorUrl, copyToClipboard } from '../lib/publicUrl';
import { Icon } from './Icon';

interface GeneratorCardProps {
    generator: Generator;
    orgSlug: string | null;
    leadsCount?: number;
}

export function GeneratorCard({ generator, orgSlug, leadsCount = 0 }: GeneratorCardProps) {
    const industry = getIndustry(generator.industry);
    const industryLabel = getIndustryLabel(generator.industry, generator.industry_custom);
    const [copied, setCopied] = useState(false);
    const createdAt = new Date(generator.created_at).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });

    const publicUrl =
        generator.is_published && orgSlug
            ? buildPublicGeneratorUrl(orgSlug, generator.slug)
            : null;

    const handleCopy = async () => {
        if (!publicUrl) return;
        const ok = await copyToClipboard(publicUrl);
        if (ok) {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
        }
    };

    const published = generator.is_published;

    return (
        <article className={`gen-card${published ? ' is-published' : ''}`}>
            <span className="gen-card-rail" aria-hidden />

            <header className="gen-card-head">
                <div className="gen-card-icon" aria-hidden>
                    <Icon name={industry.icon} size={22} />
                </div>
                <span
                    className={`gen-card-status${published ? ' is-published' : ''}`}
                    title={published ? 'Publié' : 'Brouillon'}
                >
                    <span className="gen-card-status-dot" aria-hidden />
                    {published ? 'Publié' : 'Brouillon'}
                </span>
            </header>

            <div className="gen-card-body">
                <h3 className="gen-card-name">{generator.name}</h3>
                <div className="gen-card-meta">
                    <span className="gen-card-sector">{industryLabel}</span>
                    <span className="gen-card-meta-sep" aria-hidden>·</span>
                    <span>Créé le {createdAt}</span>
                </div>
            </div>

            {leadsCount > 0 && (
                <Link
                    to={`/generator/${generator.id}/leads`}
                    className="gen-card-leads-link"
                >
                    <span className="gen-card-leads-icon" aria-hidden>
                        <Icon name="users" size={14} />
                    </span>
                    <span className="gen-card-leads-text">
                        <strong>{leadsCount}</strong> demande{leadsCount > 1 ? 's' : ''} reçue{leadsCount > 1 ? 's' : ''}
                    </span>
                    <Icon name="chevron-right" size={14} />
                </Link>
            )}

            <footer className="gen-card-foot">
                <Link
                    to={`/generator/${generator.id}/edit`}
                    className="gen-card-cta"
                >
                    Configurer
                    <Icon name="chevron-right" size={14} strokeWidth={2.2} />
                </Link>

                {publicUrl && (
                    <button
                        type="button"
                        className={`gen-card-action${copied ? ' is-done' : ''}`}
                        onClick={handleCopy}
                        title={copied ? 'Lien copié' : 'Copier le lien public'}
                        aria-label={copied ? 'Lien copié' : 'Copier le lien public'}
                    >
                        {copied ? (
                            <Icon name="check" size={15} strokeWidth={2.2} />
                        ) : (
                            <svg
                                width="15"
                                height="15"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden
                            >
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                        )}
                    </button>
                )}
            </footer>
        </article>
    );
}
